import { apiFiles } from "@/api/file.api.ts";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { getEmailPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { useFileDropzone } from "@/hooks/use-file-dropzone.hook.ts";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Box,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  FileDropzone,
  FontAwesomeIcon,
  Input,
  ListItem,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmailIntegrations } from "@/queries/integrations/use-emails.query.ts";
import {
  useCareerSiteDelete,
  useCareerSiteDetails,
  useCareerSiteSave,
} from "@/queries/recruit/use-career-sites.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { WebpUploadableImageFileTypes } from "@/types/blog.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

interface CareerSiteEditModalProps extends ModalProps {
  careerSiteId?: number | undefined;
}

export function CareerSiteEditModal({ careerSiteId, ...props }: CareerSiteEditModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close, openModal } = useModals();
  const { data: careerSite } = useCareerSiteDetails(careerSiteId, !!careerSiteId);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { handleErrors } = useErrorHandle();
  const { mutateAsync: deleteCareerSite } = useCareerSiteDelete();
  const { showInfo } = useToasts();
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const supportedFormats = Object.keys(WebpUploadableImageFileTypes).join(", ").toLowerCase();
  const { data: emailIntegrations, isLoading: isEmailIntegrationsLoading } = useEmailIntegrations();
  const emailPlaceholderOptions = useMemo(() => getEmailPlaceholderOptions(t), [t]);

  const inactiveFromEmails =
    emailIntegrations
      ?.filter((integration) => !integration.is_active)
      .map(({ identity }) => identity) ?? [];

  const careerSiteSchema = z
    .object({
      title: z.string().min(1, t("errors:field_required")),
      page_title: z.string().min(1, t("errors:field_required")),
      subheader: z.string().optional(),
      url: z.string().min(1, t("errors:field_required")),
      is_published: z.boolean({ error: t("errors:field_required") }),
      background_color: z.string().optional(),
      button_color: z.string().optional(),
      logo_file_uuid: z.string().nullable().optional(),
      logo_url: z.string().nullable().optional(),
      auto_reply_enabled: z.boolean().optional(),
      auto_reply_from_email: z.string().nullable().optional(),
      auto_reply_subject: z.string().nullable().optional(),
      auto_reply_body: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.auto_reply_enabled) return;

      if (!data.auto_reply_from_email) {
        ctx.addIssue({
          code: "custom",
          path: ["auto_reply_from_email"],
          message: t("errors:field_required"),
        });
      } else if (inactiveFromEmails.includes(data.auto_reply_from_email)) {
        ctx.addIssue({
          code: "custom",
          path: ["auto_reply_from_email"],
          message: "",
        });
      }
      if (!data.auto_reply_subject) {
        ctx.addIssue({
          code: "custom",
          path: ["auto_reply_subject"],
          message: t("errors:field_required"),
        });
      }
      if (!data.auto_reply_body || data.auto_reply_body.replace(/<[^>]*>/g, "").trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["auto_reply_body"],
          message: t("errors:field_required"),
        });
      }
    });

  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(careerSiteSchema),
    defaultValues: {
      background_color: "#ffffff",
      button_color: "#000000",
    },
  });

  const { mutateAsync, isPending } = useCareerSiteSave(control);

  const logoUrl = watch("logo_url");
  const autoReplyEnabled = watch("auto_reply_enabled");
  const autoReplyFromEmail = watch("auto_reply_from_email");

  const emailIntegrationOptions =
    emailIntegrations?.map((integration) => ({
      id: integration.identity,
      title: integration.identity,
    })) || ([] as SelectItem[]);

  const hasNoEmailIntegrations =
    !isEmailIntegrationsLoading && emailIntegrations?.length === 0 && !autoReplyFromEmail;
  const selectedIntegrationIsInactive = inactiveFromEmails.includes(autoReplyFromEmail ?? "");

  useEffect(() => {
    reset({
      title: careerSite?.title ?? "",
      page_title: careerSite?.page_title ?? "",
      subheader: careerSite?.subheader ?? "",
      is_published: !!careerSite?.is_published,
      background_color: careerSite?.background_color ?? "",
      button_color: careerSite?.button_color ?? "",
      url: careerSite?.slug ?? "",
      logo_file_uuid: careerSite?.logo_file_uuid,
      logo_url: careerSite?.logo_url,
      auto_reply_enabled: !!careerSite?.auto_reply_enabled,
      auto_reply_from_email: careerSite?.auto_reply_from_email ?? "",
      auto_reply_subject: careerSite?.auto_reply_subject ?? "",
      auto_reply_body: careerSite?.auto_reply_body ?? "",
    });
  }, [careerSite]);

  useEffect(() => {
    const logoFileUUID = careerSite?.logo_file_uuid;

    if (!logoFileUUID) return;

    let isMounted = true;

    const loadLogoPreview = async () => {
      const { data: signedLink, error } = await apiFiles.getLink(logoFileUUID, 1);

      if (!isMounted) return;

      if (signedLink?.download_url) {
        setValue("logo_url", signedLink.download_url);
      } else if (error) {
        handleErrors(error);
      }
    };

    loadLogoPreview();

    return () => {
      isMounted = false;
    };
  }, [careerSite?.logo_file_uuid, handleErrors, setValue]);

  const url = watch("url");

  const onSubmit = handleSubmit(async (data) => {
    await mutateAsync({
      id: careerSiteId,
      payload: {
        page_title: data.page_title,
        title: data.title,
        subheader: data.subheader ?? "",
        logo_file_uuid: data.logo_file_uuid,
        contacts: [],
        is_published: data.is_published,
        slug: transformURL(data.url),
        background_color: data.background_color ?? "",
        button_color: data.button_color ?? "",
        auto_reply_enabled: !!data.auto_reply_enabled,
        auto_reply_from_email: data.auto_reply_from_email || null,
        auto_reply_subject: data.auto_reply_subject || null,
        auto_reply_body: data.auto_reply_body || null,
      },
    });

    close(id);
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", ApiFilePathTypes.career_site);
        formData.append("is_public", "true");
        formData.append("save_original_name", "true");

        setIsUploading(true);

        const { data, error } = await apiFiles.add(formData);

        if (data) {
          const { data: signedLink, error: linkError } = await apiFiles.getLink(data.uuid, 1);

          if (!signedLink?.download_url) {
            handleErrors(linkError);
            setIsUploading(false);
            return;
          }

          setValue("logo_file_uuid", data?.uuid);
          setValue("logo_url", signedLink.download_url);
        } else {
          handleErrors(error);
        }

        setIsUploading(false);
      }
    },
    [careerSite]
  );

  const handleDelete = async () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (!careerSiteId) return;

        try {
          const result = await deleteCareerSite(careerSiteId);

          if (result) showInfo({ title: t("career_sites:page_deleted") });

          analyticsService.trackEvent(analyticEvents.careerSites.deleted, {
            [analyticProperties.id]: id,
          });
        } catch {
          return;
        }

        close(id);
      },
    });
  };

  const transformURL = (url: string) => {
    return url && url.trim().replace(/\s/gi, "_");
  };
  const { onDropRejected } = useFileDropzone({
    tooManyFilesMessage: t("errors:only_one_file_allowed", {
      formats: supportedFormats,
    }),
    unsupportedFileFormatMessage: t("errors:unsupported_file_format", {
      formats: supportedFormats,
    }),
  });

  return (
    <Modal
      {...props}
      withCloser={true}
      size={"lg"}
      title={
        !careerSiteId ? t("career_sites:create_modal_title") : t("career_sites:update_modal_title")
      }
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} onClick={onSubmit} isLoading={isPending}>
            {!careerSiteId ? t("career_sites:create_page") : t("career_sites:update_page")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            {...register("title")}
            type="text"
            label={t("core:name")}
            required={true}
            error={errors.title?.message}
          />
          <Input
            {...register("url", {
              onChange: (e) => {
                const filtered = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                setValue("url", filtered);
              },
            })}
            type="text"
            label={t("common:url_alias")}
            required={true}
            error={errors.url?.message}
            maxLength={20}
            pattern="^[a-z0-9_-]+$"
          />
          <Box className={"py-2 wrap-break-word text-sm"}>
            <Text
              className={"text-secondary"}
            >{`https://careers.${import.meta.env.VITE_DOMAIN}/`}</Text>
            <Text className={"font-semibold"}>{transformURL(url)}</Text>
          </Box>
          <Box className={"py-4"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  title={t("common:published")}
                  description={t("career_sites:activate_for_visible")}
                />
              )}
              name={"is_published"}
              control={control}
            />
          </Box>

          <Title size={"xs"} paddingTop paddingBottom header={t("common:text")} />
          <Input
            {...register("page_title")}
            type="text"
            label={t("career_sites:page_title")}
            required={true}
            error={errors.page_title?.message}
          />
          <Textarea
            {...register("subheader")}
            label={t("career_sites:sub_header")}
            error={errors.subheader?.message}
          />
          <Box className={"py-2 text-secondary text-sm"}>
            {t("career_sites:optional_short_description")}
          </Box>

          <Title size={"xs"} paddingTop header={t("common:visual")}></Title>
          <Title size={"sm"} paddingTop paddingBottom header={t("common:your_logo")}></Title>
          {logoUrl ? (
            <Stack gap={"sm"} direction={"horizontal"} justify={"start"} items={"start"}>
              <DropdownMenu
                trigger={
                  <img
                    src={logoUrl ?? ""}
                    alt={"logo"}
                    className={"block max-w-full h-10 cursor-pointer"}
                  />
                }
              >
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-image" />}
                  onClick={() => {
                    setValue("logo_file_uuid", null);
                    setValue("logo_url", null);
                    if (dropzoneRef.current) {
                      dropzoneRef.current.click();
                    }
                  }}
                  title={t("common:upload_logo")}
                />
                <DropdownMenuItem
                  variant={"menuDanger"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  onClick={(event) => {
                    setValue("logo_file_uuid", null);
                    setValue("logo_url", null);
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                  title={t("common:delete")}
                />
              </DropdownMenu>
            </Stack>
          ) : null}
          <FileDropzone
            onDrop={onDrop}
            accept={Object.fromEntries(
              Object.entries(WebpUploadableImageFileTypes).map(([key, value]) => [
                value,
                [`.${key.toLowerCase()}`],
              ])
            )}
            onDropRejected={onDropRejected}
            ctaActionText={t("common:logo").toLowerCase()}
            description={t("common:accepts_formats", {
              formats: supportedFormats,
            })}
            descriptionSize={"sm"}
            activeDescription={t("common:upload_file_drag_and_drop")}
            isLoading={isUploading}
            className={logoUrl ? "hidden min-h-32" : "min-h-32"}
            multiple={false}
            maxFiles={1}
            rootRef={dropzoneRef}
          />

          <Title size={"sm"} paddingTop paddingBottom header={t("common:colors")} />

          <Input
            {...register("background_color")}
            type="text"
            label={t("career_sites:background_color")}
            error={errors.background_color?.message}
          />

          <Input
            {...register("button_color")}
            type="text"
            label={t("career_sites:button_color")}
            error={errors.button_color?.message}
          />

          <Title size={"xs"} paddingTop paddingBottom header={t("career_sites:auto_reply_title")} />
          <Box className={"py-4"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Switch
                  checked={!!value}
                  onChange={onChange}
                  title={t("career_sites:auto_reply_enabled_title")}
                  description={t("career_sites:auto_reply_enabled_description")}
                />
              )}
              name={"auto_reply_enabled"}
              control={control}
            />
          </Box>

          {autoReplyEnabled && (
            <Box variant={"border"} className={"pt-(--gap-md)"}>
              {hasNoEmailIntegrations ? (
                <ListItem
                  preTitle={t("core:email_from")}
                  titleSlot={
                    <Text className="text-danger font-semibold">
                      {t("ats:email_integration_missing_title")}
                    </Text>
                  }
                  caption={t("ats:email_integration_missing_description")}
                  valueSlot={
                    <ButtonLink href="/account-settings/integrations" blank variant="secondary">
                      {t("integrations:connect")}
                    </ButtonLink>
                  }
                />
              ) : (
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <Stack gap="xs">
                      <Select
                        label={t("core:email_from")}
                        isLoading={isEmailIntegrationsLoading}
                        options={emailIntegrationOptions}
                        emptyTitle={t("common:select")}
                        value={value}
                        onChange={onChange}
                        required={true}
                        renderSelectedTitle={() => (
                          <Text
                            className={selectedIntegrationIsInactive ? "text-danger" : undefined}
                          >
                            {value}
                          </Text>
                        )}
                        error={
                          selectedIntegrationIsInactive
                            ? undefined
                            : errors.auto_reply_from_email?.message
                        }
                      />
                      {selectedIntegrationIsInactive && (
                        <Text className="text-danger text-sm py-2">
                          <Trans
                            i18nKey="ats:email_integration_expired_error_with_link"
                            components={{
                              reconnect: (
                                <a
                                  href="/account-settings/integrations"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline hover:no-underline"
                                />
                              ),
                            }}
                          />
                        </Text>
                      )}
                    </Stack>
                  )}
                  control={control}
                  name={"auto_reply_from_email"}
                />
              )}

              <Controller
                control={control}
                name={"auto_reply_subject"}
                render={({ field }) => (
                  <InputWithVariableInsert
                    required
                    label={t("core:email_subject")}
                    type="text"
                    {...field}
                    value={field.value ?? ""}
                    error={errors.auto_reply_subject?.message}
                    variableOptions={emailPlaceholderOptions}
                  />
                )}
              />

              <Title size={"xs"} paddingTop paddingBottom header={t("common:message")} />

              <Controller
                control={control}
                name={"auto_reply_body"}
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholder={t("core:email_enter_body") + "...."}
                    placeholderOptions={emailPlaceholderOptions}
                    error={errors.auto_reply_body?.message}
                  />
                )}
              />
            </Box>
          )}
        </Stack>
      </form>
      {careerSiteId ? (
        <DeleteListItem onDelete={handleDelete} title={t("career_sites:confirmation_delete")} />
      ) : (
        <></>
      )}
    </Modal>
  );
}
